import React, { useEffect, useMemo, useState } from 'react';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdVisibility, MdDirectionsCar } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import StatusToggle from '../../../Components/StatusToggle';
import { vehicleApi, locationApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './BookRoom.css';
import './Vehicles.css';

function Vehicles({ role }) {
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ query: '', department: '', location: '' });
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateData, setDeactivateData] = useState({ id: null, label: '', reason: '' });
  const [editingVehicle, setEditingVehicle] = useState(null); // Track vehicle being edited

  // Location Master dropdown
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  

  // No pagination for now

  const [formData, setFormData] = useState({
    registrationNumber: '',
    ownerName: '',
    ownerPhone: '',
    vehicleType: '',
    numberOfSeats: '',
    location: '',
  });
  

  const openAddVehicle = () => {
    setShowAddVehicleModal(true);
    setError('');
    setSuccessMessage('');
    // Prefill location from page filters if present
    if (filters.location) {
      setFormData((prev) => ({ ...prev, location: filters.location }));
    }
  };

  const closeAddVehicle = () => {
    setShowAddVehicleModal(false);
    setFormData({
      registrationNumber: '',
      ownerName: '',
      ownerPhone: '',
      vehicleType: '',
      numberOfSeats: '',
      location: '',
    });
    setError('');
    setSuccessMessage('');
    setEditingVehicle(null); // Clear editing state
  };

  useEffect(() => {
    // Load vehicles and locations on mount
    loadVehicles();
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await locationApi.getAllActiveLocationNames();
      const locationNames = response?.data || response || [];
      setAvailableLocations(locationNames);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setAvailableLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const loadVehicles = async () => {
    try {
      setIsTableLoading(true);
      setError('');
      const data = await vehicleApi.getAllVehicles();
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (Array.isArray(data?.data)) list = data.data;
      else if (Array.isArray(data?.content)) list = data.content;
      else list = [];
      setVehicles(list);
      // initialize statuses
      const initialStatuses = {};
      list.forEach((v) => {
        initialStatuses[v.id || v.registrationNumber] = v.status || 'ACTIVE';
      });
      setVehicleStatuses(initialStatuses);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to fetch vehicles');
    } finally {
      setIsTableLoading(false);
    }
  };

  // Drop locations will be entered manually; no route API fetch required

  // Filtered and paginated vehicles
  const filteredVehicles = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return [];
    const query = (filters.query || '').toLowerCase().trim();
    const loc = (filters.location || '').toLowerCase();
    return vehicles.filter((v) => {
      const reg = (v.registrationNumber || '').toLowerCase();
      const owner = (v.ownerName || '').toLowerCase();
      const type = (v.vehicleType || '').toLowerCase();
      const hasLoc = !loc || (Array.isArray(v.dropStations) && v.dropStations.some((d) => (d || '').toLowerCase() === loc));
      const queryOk = !query || reg.includes(query) || owner.includes(query) || type.includes(query);
      return queryOk && hasLoc;
    });
  }, [vehicles, filters]);

  const totalItems = useMemo(() => filteredVehicles.length, [filteredVehicles]);

  const handleStatusToggle = async (vehicle, nextIsActive) => {
    const key = vehicle.id || vehicle.registrationNumber;
    const newStatus = nextIsActive ? 'ACTIVE' : 'INACTIVE';
    if (!nextIsActive) {
      const label = vehicle.registrationNumber || 'this vehicle';
      setDeactivateData({ id: vehicle.id, label, reason: '' });
      setShowDeactivateModal(true);
      return;
    }
    try {
      await vehicleApi.updateVehicleStatus(vehicle.id, newStatus);
      setVehicleStatuses((prev) => ({ ...prev, [key]: newStatus }));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to update status');
    }
  };

  const handleEdit = (v) => {
    setEditingVehicle(v); // Set the vehicle being edited
    setShowAddVehicleModal(true);
    setFormData({
      registrationNumber: v.registrationNumber || '',
      ownerName: v.ownerName || '',
      ownerPhone: v.ownerPhone || '',
      vehicleType: v.vehicleType || '',
      numberOfSeats: String(v.numberOfSeats ?? ''),
      location: v.location || '',
    });
    setError('');
    setSuccessMessage('');
  };

  const handleDelete = (v) => {
    setVehicleToDelete(v);
  };

  const confirmDeleteVehicle = async () => {
    if (!vehicleToDelete) return;
    try {
      await vehicleApi.deleteVehicle(vehicleToDelete.id);
      await loadVehicles();
      toast.success('Vehicle deleted successfully');
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to delete vehicle';
      setError(msg);
      toast.error(msg);
    } finally {
      setVehicleToDelete(null);
    }
  };

  const cancelDeleteVehicle = () => setVehicleToDelete(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    const isEditMode = editingVehicle !== null;

    try {
      const payload = {
        ...formData,
        numberOfSeats: formData.numberOfSeats ? Number(formData.numberOfSeats) : undefined,
        location: formData.location || undefined,
      };

      if (isEditMode) {
        // UPDATE existing vehicle
        await vehicleApi.updateVehicle(editingVehicle.id, payload);
        setSuccessMessage('Vehicle updated successfully');

        toast.success('Vehicle updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        // ADD new vehicle
        await vehicleApi.addVehicle(payload);
        setSuccessMessage('Vehicle added successfully');

        toast.success('Vehicle added successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      // Refresh list
      await loadVehicles();
      setTimeout(() => {
        closeAddVehicle();
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || (isEditMode ? 'Failed to update vehicle' : 'Failed to add vehicle');
      setError(errorMsg);

      // Show error toast
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-view vehicles-view">
      <PageHeader breadcrumbs={["Community", "Vehicles"]} />

      {/* Compact Header with Filters */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdDirectionsCar className="title-icon" />
            Vehicles Management
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by registration, owner, or type..."
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            />
          </div>
          <button className="btn-book-compact" onClick={openAddVehicle}>
            <MdAdd />
            <span>Add Vehicle</span>
          </button>
        </div>
      </div>

      <div className="table card">
        <div className="table-head">
          <div className="cell">Reg. Number</div>
          <div className="cell">Owner</div>
          <div className="cell">Phone</div>
          <div className="cell">Type</div>
          <div className="cell">Seats</div>
          <div className="cell">Status</div>
          <div className="cell" style={{ textAlign: 'right' }}>Actions</div>
        </div>
        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isTableLoading && filteredVehicles && filteredVehicles.length > 0 && filteredVehicles.map((v, idx) => (
          <div className="table-row" key={idx}>
            <div className="cell">{v.registrationNumber || '-'}</div>
            <div className="cell">{v.ownerName || '-'}</div>
            <div className="cell">{v.ownerPhone || '-'}</div>
            <div className="cell">{v.vehicleType || '-'}</div>
            <div className="cell">{v.numberOfSeats ?? '-'}</div>
            <div className="cell">
              <StatusToggle
                id={`vehicle-status-${v.id || v.registrationNumber}`}
                isActive={(vehicleStatuses[v.id || v.registrationNumber] || v.status || 'ACTIVE') === 'ACTIVE'}
                onToggle={(next) => handleStatusToggle(v, next)}
              />
            </div>
            <div className="cell" style={{ textAlign: 'right', gap: '8px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-action-compact btn-view" onClick={() => setShowAddVehicleModal(true) || setFormData({ registrationNumber: v.registrationNumber || '', ownerName: v.ownerName || '', ownerPhone: v.ownerPhone || '', vehicleType: v.vehicleType || '', numberOfSeats: String(v.numberOfSeats ?? ''), location: v.location || '' })} title="View">
                <MdVisibility />
              </button>
              <button className="btn-action-compact btn-edit" onClick={() => handleEdit(v)} title="Edit">
                <MdEdit />
              </button>
              <button className="btn-action-compact btn-delete" onClick={() => handleDelete(v)} title="Delete">
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {!isTableLoading && (!filteredVehicles || filteredVehicles.length === 0) && (
          <div className="table-row">
            <div className="cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '16px' }}>
              No vehicles found.
            </div>
          </div>
        )}
      </div>

      {/* Pagination removed as requested */}

      {showAddVehicleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
              <button
                className="modal-close"
                onClick={closeAddVehicle}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="registrationNumber">Registration Number</label>
                  <input
                    type="text"
                    id="registrationNumber"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="vehicleType">Vehicle Type</label>
                  <select
                    id="vehicleType"
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select type (optional)</option>
                    <option value="Bus">Bus</option>
                    <option value="Car">Car</option>
                    <option value="Shuttle">Shuttle</option>
                  </select>
                </div>
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                disabled={isLoadingLocations}
              >
                <option value="">
                  {isLoadingLocations ? 'Loading locations...' : 'Select location'}
                </option>
                {availableLocations.map(locationName => (
                  <option key={locationName} value={locationName}>
                    {locationName}
                  </option>
                ))}
              </select>
              {availableLocations.length === 0 && !isLoadingLocations && (
                <small style={{ color: '#e74c3c', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  No active locations found. Please add locations in Location Master.
                </small>
              )}
            </div>
              </div>

          


          <div className="form-row">
            <div className="form-group">
              <label htmlFor="numberOfSeats">Number of Seats</label>
              <input
                type="number"
                id="numberOfSeats"
                name="numberOfSeats"
                value={formData.numberOfSeats}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>
            
          </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ownerName">Owner Name</label>
                  <input
                    type="text"
                    id="ownerName"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ownerPhone">Owner Phone</label>
                  <input
                    type="tel"
                    id="ownerPhone"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

          

              {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '15px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="success-message" style={{ color: 'green', marginBottom: '15px', padding: '10px', backgroundColor: '#e8f5e8', borderRadius: '4px' }}>
                  {successMessage}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={closeAddVehicle}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (editingVehicle ? 'Updating...' : 'Saving...') : (editingVehicle ? 'Update Vehicle' : 'Add Vehicle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {vehicleToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close"
                onClick={cancelDeleteVehicle}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                {`Are you sure you want to delete vehicle ${vehicleToDelete.registrationNumber || ''}?`}
              </p>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelDeleteVehicle}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteVehicle}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeactivateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Deactivate Vehicle</h3>
              <button
                className="modal-close"
                onClick={() => { setShowDeactivateModal(false); setDeactivateData({ id: null, label: '', reason: '' }); }}
              >
                ×
              </button>
            </div>
            <div className="deactivate-form">
              <div className="warning-message">
                <strong>Warning:</strong> You are about to deactivate <strong>{deactivateData.label}</strong>.
                This action will prevent it from being assigned or used.
              </div>
              <div className="form-group">
                <label htmlFor="deactivateVehicleReason">
                  Reason for deactivation <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="deactivateVehicleReason"
                  name="reason"
                  value={deactivateData.reason}
                  onChange={(e) => setDeactivateData((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Please provide a reason for deactivating this vehicle..."
                  rows="4"
                  className="deactivate-reason-textarea"
                  required
                />
              </div>
              {error && (
                <div className="error-message">{error}</div>
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => { setShowDeactivateModal(false); setDeactivateData({ id: null, label: '', reason: '' }); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-danger"
                  onClick={async () => {
                    if (!deactivateData.reason.trim()) {
                      toast.warn('Please provide a reason for deactivating the vehicle.');
                      return;
                    }
                    try {
                      await vehicleApi.updateVehicleStatus(deactivateData.id, 'INACTIVE');
                      const key = deactivateData.id;
                      setVehicleStatuses((prev) => ({ ...prev, [key]: 'INACTIVE' }));
                      setShowDeactivateModal(false);
                      setDeactivateData({ id: null, label: '', reason: '' });
                    } catch (e) {
                      setError(e.response?.data?.message || e.message || 'Failed to update status');
                    }
                  }}
                >
                  Deactivate Vehicle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vehicles;


